"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  ClipboardCheck,
  FileText,
  Gavel,
  Handshake,
  Landmark,
  MessageSquare,
  Scale,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react"
import { Textarea } from "./ui/textarea"
import "../styles/components/ConflictDetails.css"

const toArray = (value) => (Array.isArray(value) ? value : [])

const formatDate = (value) => {
  if (!value) return "Not recorded"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Not recorded"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)
}

const prettyToken = (value, fallback = "Not specified") => {
  const text = String(value || "").trim()
  if (!text) return fallback
  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
}

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  if (text.length <= 26) return text
  return `${text.slice(0, 10)}...${text.slice(-8)}`
}

const getProgress = (current, required) => {
  const needed = Number(required) > 0 ? Number(required) : 1
  return Math.min(100, Math.max(0, (Number(current || 0) / needed) * 100))
}

const statusClass = (status, isResolved) => {
  if (isResolved) return "is-resolved"
  const normalized = String(status || "").toLowerCase()
  if (normalized.includes("concern") || normalized.includes("blocked")) return "is-concern"
  if (normalized.includes("proposal") || normalized.includes("review")) return "is-review"
  return "is-open"
}

const getStageItems = (conflict) => {
  const hasRepresentatives = toArray(conflict.participants).some((item) => item.role === "Representative")
  const hasResolvers = Number(conflict.acceptedResolverCount || 0) >= Number(conflict.requiredResolverCount || 3)
  const hasSolutionPieces = toArray(conflict.solutionPieces).length > 0
  const hasProposal = Boolean(conflict.proposedResolution)
  const isResolved = conflict.isResolved || conflict.status === "Resolved"

  return [
    { label: "Parties named", complete: toArray(conflict.parties).length > 0 },
    { label: "Representatives", complete: hasRepresentatives },
    { label: "Resolver panel", complete: hasResolvers },
    { label: "Solution pieces", complete: hasSolutionPieces },
    { label: "Final proposal", complete: hasProposal },
    { label: "Resolved", complete: isResolved },
  ]
}

const ConflictBadge = ({ children, tone = "neutral" }) => (
  <span className={`conflict-badge ${tone}`}>{children}</span>
)

const ConflictButton = ({ children, variant = "primary", className = "", ...props }) => (
  <button type="button" className={`conflict-button ${variant} ${className}`.trim()} {...props}>
    {children}
  </button>
)

const Section = ({ icon: Icon, kicker, title, action, children, className = "" }) => (
  <section className={`conflict-section ${className}`.trim()}>
    <div className="conflict-section-header">
      <div>
        {kicker && (
          <span className="conflict-kicker">
            {Icon && <Icon className="h-4 w-4" />}
            {kicker}
          </span>
        )}
        <h2>{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
)

const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="conflict-empty-state">
    {Icon && <Icon className="h-5 w-5" />}
    <div>
      <strong>{title}</strong>
      {body && <p>{body}</p>}
    </div>
  </div>
)

const MetricCard = ({ icon: Icon, label, value, detail }) => (
  <div className="conflict-metric">
    <span className="conflict-metric-icon">{Icon && <Icon className="h-5 w-5" />}</span>
    <div>
      <p>{label}</p>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  </div>
)

const ConflictDetails = () => {
  const { conflictId } = useParams()
  const { getAccessTokenSilently } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL

  const [conflict, setConflict] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  const [responses, setResponses] = useState({})
  const [proposal, setProposal] = useState("")
  const [participant, setParticipant] = useState({ userId: "", role: "Representative", conflictPartyId: "" })
  const [resolverPanel, setResolverPanel] = useState("")
  const [solutionPiece, setSolutionPiece] = useState({ pieceType: "Remedy", title: "", description: "" })
  const [pieceComments, setPieceComments] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const fetchConflict = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/${conflictId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error("Failed to fetch conflict")
      }
      const data = await res.json()
      setConflict(data)
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [apiUrl, conflictId, getAccessTokenSilently])

  useEffect(() => {
    fetchConflict()
  }, [fetchConflict])

  const runAction = async (action, successMessage) => {
    setSubmitting(true)
    setActionError("")
    setNotice("")
    try {
      await action()
      setNotice(successMessage)
      await fetchConflict()
    } catch (err) {
      setActionError(err.message || "That action could not be completed.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleResponseChange = (id, value) => {
    setResponses((prev) => ({ ...prev, [id]: value }))
  }

  const submitResponse = async (argumentId) => {
    const content = responses[argumentId]
    if (!content?.trim()) return

    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/arguments/${argumentId}/responses`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      })
      if (!res.ok) throw new Error("Could not submit that response.")
      setResponses((prev) => ({ ...prev, [argumentId]: "" }))
    }, "Response added.")
  }

  const submitProposal = async () => {
    if (!proposal.trim()) return

    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/${conflictId}/resolution-proposal`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resolution: proposal }),
      })
      if (!res.ok) throw new Error("Could not submit the resolution proposal.")
      setProposal("")
    }, "Resolution proposal submitted.")
  }

  const voteOnProposal = async (supportsResolution) => {
    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/${conflictId}/resolution-votes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ supportsResolution }),
      })
      if (!res.ok) throw new Error("Could not record that vote.")
    }, supportsResolution ? "Support recorded." : "Concern recorded.")
  }

  const addParticipant = async () => {
    if (!participant.userId.trim()) return

    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/${conflictId}/participants`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: participant.userId,
          role: participant.role,
          conflictPartyId: participant.role === "Representative" ? Number(participant.conflictPartyId) : null,
          selectionMethod: participant.role === "Resolver" ? "RandomParentPool" : "GroupVote",
          hasAccepted: participant.role === "Representative",
        }),
      })
      if (!res.ok) throw new Error("Could not add that participant.")
      setParticipant({ userId: "", role: "Representative", conflictPartyId: "" })
    }, "Participant added.")
  }

  const selectResolverPanel = async () => {
    const resolverUserIds = resolverPanel
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
    if (resolverUserIds.length === 0) return

    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/${conflictId}/resolver-panel`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resolverUserIds }),
      })
      if (!res.ok) throw new Error("Could not select the resolver panel.")
      setResolverPanel("")
    }, "Resolver panel updated.")
  }

  const addSolutionPiece = async () => {
    if (!solutionPiece.title.trim() || !solutionPiece.description.trim()) return

    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/${conflictId}/solution-pieces`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(solutionPiece),
      })
      if (!res.ok) throw new Error("Could not add the solution piece.")
      setSolutionPiece({ pieceType: "Remedy", title: "", description: "" })
    }, "Solution piece added.")
  }

  const approveSolutionPiece = async (pieceId, isApproved) => {
    await runAction(async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const res = await fetch(`${apiUrl}/api/conflicts/solution-pieces/${pieceId}/approval`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isApproved, comment: pieceComments[pieceId] || "" }),
      })
      if (!res.ok) throw new Error("Could not record that review.")
      setPieceComments((prev) => ({ ...prev, [pieceId]: "" }))
    }, isApproved ? "Approval recorded." : "Concern recorded.")
  }

  const view = useMemo(() => {
    if (!conflict) return null
    const argumentsList = toArray(conflict.arguments)
    const parties = toArray(conflict.parties)
    const participants = toArray(conflict.participants)
    const solutionPieces = toArray(conflict.solutionPieces)
    const representatives = participants.filter((item) => item.role === "Representative")
    const resolvers = participants.filter((item) => item.role === "Resolver")
    const isResolved = conflict.isResolved || conflict.status === "Resolved"
    const hasProposal = Boolean(conflict.proposedResolution)
    const supportCount = Number(conflict.resolutionSupportCount || 0)
    const concernCount = Number(conflict.resolutionConcernCount || 0)
    const requiredResolvers = Number(conflict.requiredResolverCount || 3)
    const acceptedResolvers = Number(conflict.acceptedResolverCount || 0)
    const stageItems = getStageItems(conflict)
    const completedStages = stageItems.filter((item) => item.complete).length

    return {
      argumentsList,
      parties,
      representatives,
      resolvers,
      solutionPieces,
      isResolved,
      hasProposal,
      supportCount,
      concernCount,
      requiredResolvers,
      acceptedResolvers,
      stageItems,
      completedStages,
    }
  }, [conflict])

  if (loading) {
    return (
      <main className="conflict-page-shell">
        <div className="conflict-loading-panel">
          <div className="conflict-spinner" />
          <p>Loading conflict workspace...</p>
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main className="conflict-page-shell">
        <div className="conflict-error-panel">
          <AlertTriangle className="h-6 w-6" />
          <div>
            <h1>Could not load this conflict</h1>
            <p>{error}</p>
            <ConflictButton onClick={fetchConflict}>Retry</ConflictButton>
          </div>
        </div>
      </main>
    )
  }

  if (!conflict || !view) return null

  return (
    <main className="conflict-page-shell">
      <div className="conflict-page">
        <Link className="conflict-back-link" to={conflict.groupId ? `/group-members/${conflict.groupId}?tab=problems` : "/dashboard"}>
          <ArrowLeft className="h-4 w-4" />
          Back to group work
        </Link>

        <section className="conflict-hero">
          <div className="conflict-hero-content">
            <span className="conflict-kicker">
              <Scale className="h-4 w-4" />
              Conflict resolution workspace
            </span>
            <div className="conflict-title-row">
              <h1>{conflict.title}</h1>
              <ConflictBadge tone={statusClass(conflict.status, view.isResolved)}>
                {conflict.status || (view.isResolved ? "Resolved" : "Open")}
              </ConflictBadge>
            </div>
            <p>{conflict.description}</p>
            <div className="conflict-hero-meta">
              <span>Filed {formatDate(conflict.createdOn)}</span>
              <span>Created by {shortUser(conflict.createdBy)}</span>
              <span>{prettyToken(conflict.conflictType, "Coordination conflict")}</span>
            </div>
          </div>

          <aside className="conflict-progress-card">
            <div className="conflict-progress-ring" style={{ "--progress": `${(view.completedStages / view.stageItems.length) * 360}deg` }}>
              <strong>{view.completedStages}/{view.stageItems.length}</strong>
              <span>steps</span>
            </div>
            <div className="conflict-stage-list">
              {view.stageItems.map((item) => (
                <span key={item.label} className={item.complete ? "is-complete" : ""}>
                  {item.complete ? <CheckCircle2 className="h-4 w-4" /> : <CircleDot className="h-4 w-4" />}
                  {item.label}
                </span>
              ))}
            </div>
          </aside>
        </section>

        {(notice || actionError) && (
          <div className={actionError ? "conflict-notice error" : "conflict-notice success"}>
            {actionError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
            <span>{actionError || notice}</span>
          </div>
        )}

        <section className="conflict-metrics-grid">
          <MetricCard icon={MessageSquare} label="Arguments" value={view.argumentsList.length} detail="Submitted positions" />
          <MetricCard icon={Users} label="Resolvers" value={`${view.acceptedResolvers}/${view.requiredResolvers}`} detail="Accepted panel seats" />
          <MetricCard icon={ClipboardCheck} label="Solution Pieces" value={view.solutionPieces.length} detail="Findings and remedies" />
          <MetricCard icon={Handshake} label="Resolution Vote" value={`${view.supportCount} / ${view.concernCount}`} detail="Support / concern" />
        </section>

        {conflict.resolution && (
          <section className="conflict-resolution-banner">
            <ShieldCheck className="h-6 w-6" />
            <div>
              <span>Resolution accepted</span>
              <p>{conflict.resolution}</p>
              {conflict.resolutionDecidedOn && <small>Decided {formatDate(conflict.resolutionDecidedOn)}</small>}
            </div>
          </section>
        )}

        <div className="conflict-layout">
          <div className="conflict-main-column">
            <Section icon={Landmark} kicker="Parties and authority" title="Who is involved">
              <div className="conflict-party-grid">
                {view.parties.length === 0 ? (
                  <EmptyState icon={Landmark} title="No parties listed" body="This conflict has not recorded formal parties yet." />
                ) : (
                  view.parties.map((party) => (
                    <article className="conflict-party-card" key={party.id}>
                      <ConflictBadge tone={party.partyRole === "Responding" ? "warm" : "cool"}>{prettyToken(party.partyRole)}</ConflictBadge>
                      <h3>{party.name}</h3>
                      <p>{prettyToken(party.partyType)}{party.groupId ? ` ${party.groupId}` : ""}</p>
                      {(party.objectType || party.objectId) && (
                        <small>{prettyToken(party.objectType)} {party.objectId || ""}</small>
                      )}
                    </article>
                  ))
                )}
              </div>
              <div className="conflict-authority-strip">
                <span><Gavel className="h-4 w-4" /> Jurisdiction</span>
                <strong>{conflict.jurisdictionGroupId ? `Group ${conflict.jurisdictionGroupId}` : "Not assigned"}</strong>
                {(conflict.sourceObjectType || conflict.targetObjectType) && (
                  <p>
                    {prettyToken(conflict.sourceObjectType, "Source")} {conflict.sourceObjectId || ""} <ChevronRight className="h-4 w-4" /> {prettyToken(conflict.targetObjectType, "Target")} {conflict.targetObjectId || ""}
                  </p>
                )}
              </div>
            </Section>

            <Section icon={MessageSquare} kicker="Arguments" title="Positions and responses">
              <div className="conflict-argument-timeline">
                {view.argumentsList.length === 0 ? (
                  <EmptyState icon={MessageSquare} title="No arguments yet" body="Formal positions will appear here as they are submitted." />
                ) : (
                  view.argumentsList.map((arg) => (
                    <article className="conflict-argument-card" key={arg.id}>
                      <div className="conflict-argument-header">
                        <div>
                          <span>Argument</span>
                          <strong>{shortUser(arg.createdBy)}</strong>
                        </div>
                        <small>{formatDate(arg.createdOn)}</small>
                      </div>
                      <p>{arg.content}</p>
                      {arg.responseDueOn && (
                        <div className="conflict-due-line">
                          Response due {formatDate(arg.responseDueOn)}
                        </div>
                      )}
                      <div className="conflict-response-list">
                        {toArray(arg.responses).map((resp) => (
                          <div className="conflict-response-card" key={resp.id}>
                            <span>{shortUser(resp.createdBy)} responded {formatDate(resp.createdOn)}</span>
                            <p>{resp.content}</p>
                          </div>
                        ))}
                      </div>
                      {!view.isResolved && (
                        <div className="conflict-inline-form">
                          <Textarea
                            placeholder="Write a response that clarifies facts, concerns, or possible common ground..."
                            value={responses[arg.id] || ""}
                            onChange={(event) => handleResponseChange(arg.id, event.target.value)}
                          />
                          <ConflictButton variant="secondary" disabled={submitting || !responses[arg.id]?.trim()} onClick={() => submitResponse(arg.id)}>
                            Submit response
                          </ConflictButton>
                        </div>
                      )}
                    </article>
                  ))
                )}
              </div>
            </Section>

            <Section icon={Sparkles} kicker="Solution building" title="Findings, remedies, and follow-up">
              <div className="conflict-solution-list">
                {view.solutionPieces.length === 0 ? (
                  <EmptyState icon={Sparkles} title="No solution pieces yet" body="Add small, reviewable pieces so representatives can approve or flag concerns clearly." />
                ) : (
                  view.solutionPieces.map((piece) => (
                    <article className="conflict-solution-card" key={piece.id}>
                      <div className="conflict-solution-header">
                        <div>
                          <ConflictBadge tone="cool">{prettyToken(piece.pieceType)}</ConflictBadge>
                          <h3>{piece.title}</h3>
                          {piece.proposedBy && <span>Proposed by {shortUser(piece.proposedBy)}</span>}
                        </div>
                        <div className="conflict-solution-score">
                          <strong>{piece.approvalCount || 0}</strong>
                          <span>approve</span>
                          <strong>{piece.concernCount || 0}</strong>
                          <span>concern</span>
                        </div>
                      </div>
                      <p>{piece.description}</p>
                      {toArray(piece.approvals).length > 0 && (
                        <div className="conflict-approval-list">
                          {toArray(piece.approvals).map((approval) => (
                            <div className={approval.isApproved ? "approval approve" : "approval concern"} key={approval.id}>
                              {approval.isApproved ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                              <span>{shortUser(approval.userId)} {approval.isApproved ? "approved" : "raised concern"}</span>
                              {approval.comment && <p>{approval.comment}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {!view.isResolved && (
                        <div className="conflict-review-form">
                          <input
                            value={pieceComments[piece.id] || ""}
                            onChange={(event) => setPieceComments((prev) => ({ ...prev, [piece.id]: event.target.value }))}
                            placeholder="Representative note"
                          />
                          <ConflictButton disabled={submitting} onClick={() => approveSolutionPiece(piece.id, true)}>Approve</ConflictButton>
                          <ConflictButton variant="secondary" disabled={submitting} onClick={() => approveSolutionPiece(piece.id, false)}>Concern</ConflictButton>
                        </div>
                      )}
                    </article>
                  ))
                )}
              </div>

              {!view.isResolved && (
                <div className="conflict-builder">
                  <div className="conflict-form-grid">
                    <select
                      value={solutionPiece.pieceType}
                      onChange={(event) => setSolutionPiece((prev) => ({ ...prev, pieceType: event.target.value }))}
                    >
                      <option value="Finding">Finding</option>
                      <option value="Remedy">Remedy</option>
                      <option value="Concession">Concession</option>
                      <option value="FollowUp">Follow-up</option>
                      <option value="Enforcement">Enforcement</option>
                    </select>
                    <input
                      value={solutionPiece.title}
                      onChange={(event) => setSolutionPiece((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder="Solution piece title"
                    />
                  </div>
                  <Textarea
                    value={solutionPiece.description}
                    onChange={(event) => setSolutionPiece((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Describe the finding, remedy, concession, enforcement step, or follow-up..."
                  />
                  <ConflictButton disabled={submitting || !solutionPiece.title.trim() || !solutionPiece.description.trim()} onClick={addSolutionPiece}>
                    Add solution piece
                  </ConflictButton>
                </div>
              )}
            </Section>
          </div>

          <aside className="conflict-side-column">
            <Section icon={Users} kicker="Panel" title="Representatives and resolvers" className="conflict-sticky-section">
              <div className="conflict-panel-block">
                <div className="conflict-panel-heading">
                  <strong>Representatives</strong>
                  <span>{view.representatives.length}</span>
                </div>
                {view.representatives.length === 0 ? (
                  <p>No representatives selected.</p>
                ) : (
                  view.representatives.map((item) => (
                    <div className="conflict-person-row" key={item.id}>
                      <span>{shortUser(item.userId)}</span>
                      <ConflictBadge>{item.representativeVoteCount || 0} votes</ConflictBadge>
                    </div>
                  ))
                )}
              </div>

              <div className="conflict-panel-block">
                <div className="conflict-panel-heading">
                  <strong>Resolver panel</strong>
                  <span>{view.resolvers.length}</span>
                </div>
                <div className="conflict-progress-bar" aria-label="Resolver panel acceptance">
                  <span style={{ width: `${getProgress(view.acceptedResolvers, view.requiredResolvers)}%` }} />
                </div>
                {view.resolvers.length === 0 ? (
                  <p>No resolver panel selected.</p>
                ) : (
                  view.resolvers.map((item) => (
                    <div className="conflict-person-row" key={item.id}>
                      <span>{shortUser(item.userId)}</span>
                      <ConflictBadge tone={item.hasAccepted ? "is-resolved" : "neutral"}>{item.resolverCredit || 0} credit</ConflictBadge>
                    </div>
                  ))
                )}
              </div>

              {!view.isResolved && (
                <div className="conflict-participant-panel">
                  <span className="conflict-kicker"><UserPlus className="h-4 w-4" /> Add participant</span>
                  <div className="conflict-form-grid compact">
                    <select
                      value={participant.role}
                      onChange={(event) => setParticipant((prev) => ({ ...prev, role: event.target.value }))}
                    >
                      <option value="Representative">Representative</option>
                      <option value="Resolver">Resolver</option>
                    </select>
                    <select
                      value={participant.conflictPartyId}
                      onChange={(event) => setParticipant((prev) => ({ ...prev, conflictPartyId: event.target.value }))}
                      disabled={participant.role !== "Representative"}
                    >
                      <option value="">Party</option>
                      {view.parties.map((party) => (
                        <option key={party.id} value={party.id}>{party.name}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    value={participant.userId}
                    onChange={(event) => setParticipant((prev) => ({ ...prev, userId: event.target.value }))}
                    placeholder="User id"
                  />
                  <ConflictButton disabled={submitting || !participant.userId.trim()} onClick={addParticipant}>
                    Add participant
                  </ConflictButton>

                  <div className="conflict-divider" />
                  <span className="conflict-kicker"><ShieldCheck className="h-4 w-4" /> Resolver panel</span>
                  <Textarea
                    value={resolverPanel}
                    onChange={(event) => setResolverPanel(event.target.value)}
                    placeholder="Resolver user ids, comma separated"
                  />
                  <ConflictButton variant="secondary" disabled={submitting || !resolverPanel.trim()} onClick={selectResolverPanel}>
                    Select panel
                  </ConflictButton>
                </div>
              )}
            </Section>

            <Section icon={FileText} kicker="Final resolution" title="Proposal and vote">
              {view.hasProposal ? (
                <div className="conflict-proposal-card">
                  <span>Proposed by {shortUser(conflict.resolutionProposedBy)}</span>
                  <p>{conflict.proposedResolution}</p>
                  {conflict.resolutionProposedOn && <small>{formatDate(conflict.resolutionProposedOn)}</small>}
                </div>
              ) : (
                <EmptyState icon={FileText} title="No final proposal yet" body="Use the reviewed solution pieces to write the final resolution." />
              )}

              {!view.isResolved && (
                <div className="conflict-builder compact">
                  <Textarea
                    placeholder={view.hasProposal ? "Propose a clearer resolution..." : "Propose a final resolution..."}
                    value={proposal}
                    onChange={(event) => setProposal(event.target.value)}
                  />
                  <ConflictButton disabled={submitting || !proposal.trim()} onClick={submitProposal}>
                    Submit proposal
                  </ConflictButton>
                </div>
              )}

              {view.hasProposal && !view.isResolved && (
                <div className="conflict-vote-card">
                  <p>A proposal resolves when it has at least two supporting votes and more support than concerns.</p>
                  <div>
                    <ConflictButton disabled={submitting} onClick={() => voteOnProposal(true)}>
                      Support
                    </ConflictButton>
                    <ConflictButton variant="secondary" disabled={submitting} onClick={() => voteOnProposal(false)}>
                      Raise concern
                    </ConflictButton>
                  </div>
                </div>
              )}
            </Section>
          </aside>
        </div>
      </div>
    </main>
  )
}

export default ConflictDetails
